/**
 * LIBRARY mode: the cached list of persisted clips and this project's clip assignment
 * (SPEC "Model Animation Assignment"). The store never talks to IndexedDB — wave-3/4 code calls
 * the repos and feeds the results in here.
 */

import { DEFAULT_FOLDER } from '../../shared';
import type { LibraryClipSummary } from '../types';
import type { ActionContext } from './context';

/**
 * What a caller may hand `setLibraryClips`. `folder` is optional here and required on the state:
 * a summary built without one is unfiled, and the store fills in `DEFAULT_FOLDER` rather than
 * making every call site repeat it.
 */
export type LibraryClipSummaryInput = Omit<LibraryClipSummary, 'folder'> & { folder?: string };

export interface LibraryActions {
  setLibraryClips(clips: readonly LibraryClipSummaryInput[]): void;
  setSelectedClipIds(ids: string[]): void;
  /** The folder name list (excluding `DEFAULT_FOLDER`), as read from persistence. */
  setLibraryFolders(folders: string[]): void;
  toggleClipSelected(id: string): void;
  /** The open clip has been written to the library: clear the dirty flag. */
  markClipSaved(): void;
  /**
   * The open clip no longer matches anything persisted (e.g. it was deleted from the library
   * while still open): set the dirty flag without touching keyframe data. No-op when no clip is
   * open. Not undoable — dirtiness is a fact about storage, not an edit.
   */
  markClipDirty(): void;
}

export function createLibraryActions(ctx: ActionContext): LibraryActions {
  return {
    setLibraryClips(clips): void {
      const library = ctx.getState().library;
      const next: LibraryClipSummary[] = clips.map((clip) => ({
        ...clip,
        folder: clip.folder ?? DEFAULT_FOLDER,
      }));
      ctx.set({ library: { ...library, clips: next } });
    },

    setLibraryFolders(folders): void {
      const library = ctx.getState().library;
      // No-op when unchanged: `refresh()` runs after every library write, and a fresh array would
      // otherwise wake every folder-rendering subscriber for nothing.
      if (
        library.folders.length === folders.length &&
        library.folders.every((name, i) => name === folders[i])
      ) {
        return;
      }
      ctx.set({ library: { ...library, folders: [...folders] } });
    },

    setSelectedClipIds(ids): void {
      const library = ctx.getState().library;
      const unique = [...new Set(ids)];
      ctx.set({ library: { ...library, selectedClipIds: unique } });
    },

    toggleClipSelected(id): void {
      const library = ctx.getState().library;
      const selected = library.selectedClipIds.includes(id)
        ? library.selectedClipIds.filter((x) => x !== id)
        : [...library.selectedClipIds, id];
      ctx.set({ library: { ...library, selectedClipIds: selected } });
    },

    markClipSaved(): void {
      if (!ctx.getState().clipDirty) return;
      ctx.set({ clipDirty: false });
    },

    markClipDirty(): void {
      const state = ctx.getState();
      if (!state.clip || state.clipDirty) return;
      ctx.set({ clipDirty: true });
    },
  };
}
